import React, { useMemo } from 'react';

import { Plus, Trash } from 'lucide-react';
import { connect } from 'react-redux';

import { IdeaFieldTypes } from 'common/constants/ideas';
import { IdeaScoreFieldTypes } from 'common/constants/ideaScoreFields';
import generateRandomID from 'common/generateRandomID';
import HStack from 'common/ui/HStack';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import { Select } from 'common/uiV3/Select';

import type { IdeaField } from 'common/api/endpoints/idea';
import type { IdeasConfigurationState } from 'common/reducers/ideasConfiguration';
import type {
  ScoreChangeFields,
  ScoreFieldFormState,
} from 'common/subdomain/admin/v2/hooks/useIdeaFieldForm';
import type { Option } from 'common/ui/common/select/SelectCommon';

type TField = {
  tempID: string;
  fieldID: string;
  weight: number;
  type: IdeaScoreFieldTypes;
};

const hasIncompleteFields = (fields: ScoreFieldFormState['fields']) =>
  fields.some((field) => !field.fieldID);

const generateEmptyOption = (type: IdeaScoreFieldTypes): TField => ({
  tempID: generateRandomID(),
  fieldID: '',
  weight: 0,
  type,
});

const FieldInput = ({
  deletable,
  field,
  options,
  onCreateField,
  onUpdateField,
  onRemoveField,
}: {
  deletable: boolean;
  field: TField;
  options: Option[];
  onCreateField: (field: TField) => void;
  onUpdateField: (field: TField) => void;
  onRemoveField: (field: TField) => void;
}) => {
  const selectedOption = options.find((option) => option.value === field.fieldID);

  return (
    <HStack className="gap-2 items-center w-full">
      <Select
        allowClear={false}
        onValueChange={(value) => {
          if (value) {
            if (!hasIncompleteFields([field])) {
              onUpdateField({ ...field, fieldID: value });
            } else {
              onCreateField({ ...field, fieldID: value });
            }
          }
        }}
        fullWidth
        options={options}
        placeholder="Select field"
        aria-required
        value={selectedOption?.value}
      />
      <div className="w-18">
        <Input
          onChange={(value) => onUpdateField({ ...field, weight: Number(value) })}
          value={field.weight}
          type="number"
          disabled={!selectedOption}
        />
      </div>
      {deletable && (
        <Button
          variant="plain"
          color="error"
          aria-label="Remove field"
          onClick={() => onRemoveField(field)}
          iconOnly>
          <Trash />
        </Button>
      )}
    </HStack>
  );
};

type ConnectProps = {
  ideaFields: IdeaField[];
};

type OwnProps = {
  scoreFieldState: ScoreFieldFormState;
  setValue: (field: ScoreChangeFields, value: ScoreFieldFormState['fields']) => void;
};

type Props = ConnectProps & OwnProps;

function ScoreFieldInputs({ ideaFields, scoreFieldState: { fields }, setValue }: Props) {
  const fieldOptions = useMemo(
    () => fields.map((field) => ({ ...field, tempID: field.tempID || generateRandomID() })),
    [fields],
  );

  const options =
    ideaFields
      ?.filter((ideaField) =>
        [
          IdeaFieldTypes.formula,
          IdeaFieldTypes.number,
          IdeaFieldTypes.rating,
          IdeaFieldTypes.fibonacci,
          IdeaFieldTypes.range,
          IdeaFieldTypes.boolean,
        ].includes(ideaField.type),
      )
      .map((ideaField) => ({
        label: ideaField.label,
        value: ideaField.name,
      })) || [];

  const impactFields = fieldOptions.some((field) => field.type === IdeaScoreFieldTypes.impact)
    ? fieldOptions.filter((field) => field.type === IdeaScoreFieldTypes.impact)
    : [generateEmptyOption(IdeaScoreFieldTypes.impact)];

  const errortFields = fieldOptions.some((field) => field.type === IdeaScoreFieldTypes.cost)
    ? fieldOptions.filter((field) => field.type === IdeaScoreFieldTypes.cost)
    : [generateEmptyOption(IdeaScoreFieldTypes.cost)];

  const updateField = (field: TField) => {
    const updatedFields = fieldOptions.map((f) => (f.tempID === field.tempID ? field : f));
    setValue('fields', updatedFields);
  };

  const removeField = (field: TField) => {
    const updatedFields = fieldOptions.filter((f) => f.tempID !== field.tempID);
    setValue('fields', updatedFields);
  };

  const createField = (field: TField) => {
    setValue('fields', [...fieldOptions.filter((f) => f.tempID !== field.tempID), field]);
  };

  return (
    <>
      <Field>
        <Field.Label>Impact fields</Field.Label>
        <Field.Description>
          Score is calculated by multiplying each field by their weight. Then the total impact is
          divided by effort. More details can be found&nbsp;
          <a
            className="text-primary text-sm"
            target="_blank"
            rel="noopener"
            href="https://help.canny.io/en/articles/5046937-how-do-prioritization-factors-and-scoring-work">
            here
          </a>
          .
        </Field.Description>
        <Field.Content className="items-start">
          {impactFields.map((field) => (
            <FieldInput
              deletable={impactFields.length > 1}
              key={field.tempID}
              field={field}
              onUpdateField={updateField}
              onRemoveField={removeField}
              onCreateField={createField}
              options={options}
            />
          ))}
          <Button
            disabled={hasIncompleteFields(impactFields)}
            variant="plain"
            size="small"
            onClick={() => createField(generateEmptyOption(IdeaScoreFieldTypes.impact))}>
            <Plus />
            Add another field
          </Button>
        </Field.Content>
      </Field>
      <Field>
        <Field.Label>Effort fields</Field.Label>
        <Field.Content className="items-start">
          {errortFields.map((field) => (
            <FieldInput
              deletable={errortFields.length > 1}
              key={field.tempID}
              field={field}
              onUpdateField={updateField}
              onRemoveField={removeField}
              onCreateField={createField}
              options={options}
            />
          ))}
          <Button
            disabled={hasIncompleteFields(errortFields)}
            variant="plain"
            size="small"
            onClick={() => createField(generateEmptyOption(IdeaScoreFieldTypes.cost))}>
            <Plus />
            Add another field
          </Button>
        </Field.Content>
      </Field>
    </>
  );
}

export default connect(
  ({ ideasConfiguration }: { ideasConfiguration: IdeasConfigurationState }) => {
    const ideaFields = ideasConfiguration.data?.fields;
    return {
      ideaFields: ideaFields ?? [],
    };
  },
)(ScoreFieldInputs) as unknown as React.FC<OwnProps>;
